import { PuzzleSessionController } from '../../domain/puzzle/PuzzleSessionController';
import { StockfishService } from './StockfishService';
import { budgets } from '../../domain/engine/protocol';
import { classify, normalizeScore } from '../../domain/engine/uci';
export class EngineCoordinator {
  private generation = -1;
  private request = 0;
  private busy = false;
  private unsubscribe: () => void;
  private disposed = false;
  profile: 'casual' | 'strong' | 'best' = 'strong';
  constructor(
    private controller: PuzzleSessionController,
    private engine = new StockfishService(),
  ) {
    this.unsubscribe = controller.subscribe(() => this.sync());
    this.sync();
  }
  private sync() {
    const { status, session: s } = this.controller.snapshot;
    if (s.generation !== this.generation) {
      this.generation = s.generation;
      this.busy = false;
      this.engine.stop();
    }
    if (this.busy || this.disposed) return;
    if (status === 'solved' && !s.result) void this.run('classify');
    if (status === 'incorrect') void this.run('mistake');
    if (status === 'continuation-engine-turn') void this.run('opponent');
  }
  seeWhy() {
    if (this.controller.snapshot.status === 'incorrect' && !this.busy) void this.run('mistake');
  }
  private async run(kind: 'classify' | 'opponent' | 'mistake') {
    this.busy = true;
    const generation = this.controller.snapshot.session.generation,
      requestId = `${generation}:${++this.request}`;
    const status =
      kind === 'classify'
        ? 'continuation-classifying'
        : kind === 'opponent'
          ? 'continuation-engine-turn'
          : 'mistake-analyzing';
    if (!this.controller.beginRequest(status, requestId)) {
      this.busy = false;
      return;
    }
    const s = this.controller.snapshot.session,
      rules = this.controller.rules;
    try {
      if (kind === 'mistake' && rules.result) {
        this.busy = false;
        this.controller.demo(generation, requestId, []);
        return;
      }
      const result = await this.engine.search({
        fen: s.fen,
        generation,
        requestId,
        movetime:
          kind === 'classify'
            ? budgets.classification
            : kind === 'mistake'
              ? budgets.refutation
              : s.continuation?.chosen === 'finish-mate'
                ? budgets.best
                : budgets[this.profile],
        skill:
          kind === 'opponent' &&
          s.continuation?.chosen !== 'finish-mate' &&
          this.profile === 'casual'
            ? 6
            : 20,
        newGame: kind === 'classify',
      });
      if (this.disposed || !this.controller.matches(generation, requestId)) return;
      this.busy = false;
      if (kind === 'classify')
        this.controller.classify(
          generation,
          requestId,
          classify(result.infos, rules.turn, s.puzzle.solverColor),
        );
      else if (kind === 'mistake') {
        const pv = result.infos
          .filter((info) => !info.bound && info.pv[0] === result.move)
          .at(-1)?.pv;
        this.controller.demo(generation, requestId, pv?.length ? pv : [result.move]);
      } else {
        const last = result.infos.filter((i) => !i.bound).at(-1),
          score = last && normalizeScore(last, rules.turn, s.puzzle.solverColor);
        const matePresent =
          score?.mate !== undefined && score.mate > 0
            ? true
            : last && last.depth >= 10 && score?.cp !== undefined
              ? false
              : undefined;
        this.controller.engineMove(generation, requestId, result.move, matePresent);
      }
    } catch {
      if (!this.disposed && this.controller.matches(generation, requestId)) {
        this.busy = false;
        this.controller.failEngine(generation, requestId);
      }
    }
  }
  dispose() {
    this.disposed = true;
    this.unsubscribe();
    this.engine.dispose();
  }
}
